/**
 * Animated booking notification toast popup
 * Appears in bottom-right corner, auto-dismisses after 12 seconds
 */
import React, { useEffect, useState } from "react";
import { X, User, Calendar, Phone, ThumbsUp, ThumbsDown, ExternalLink } from "lucide-react";
import { useNotifications } from "../../context/NotificationContext";

const SOURCE_COLORS = {
  Website:       { dot: "bg-green-400",  bg: "bg-green-50",  text: "text-green-700",  ring: "ring-green-200" },
  Airbnb:        { dot: "bg-rose-400",   bg: "bg-rose-50",   text: "text-rose-700",   ring: "ring-rose-200" },
  "Booking.com": { dot: "bg-blue-400",   bg: "bg-blue-50",   text: "text-blue-700",   ring: "ring-blue-200" },
  Agoda:         { dot: "bg-red-400",    bg: "bg-red-50",    text: "text-red-700",    ring: "ring-red-200" },
  MakeMyTrip:    { dot: "bg-indigo-400", bg: "bg-indigo-50", text: "text-indigo-700", ring: "ring-indigo-200" },
  Expedia:       { dot: "bg-amber-400",  bg: "bg-amber-50",  text: "text-amber-700",  ring: "ring-amber-200" },
  "Hotels.com":  { dot: "bg-orange-400", bg: "bg-orange-50", text: "text-orange-700", ring: "ring-orange-200" },
  Manual:        { dot: "bg-purple-400", bg: "bg-purple-50", text: "text-purple-700", ring: "ring-purple-200" },
};

const AUTO_DISMISS_MS = 12000;

function Toast({ notification, onDismiss }) {
  const [visible,  setVisible]  = useState(false);
  const [progress, setProgress] = useState(100);

  useEffect(() => {
    // Slide in
    const t1 = setTimeout(() => setVisible(true), 50);

    // Progress bar countdown
    const step  = 100 / (AUTO_DISMISS_MS / 100);
    const timer = setInterval(() => {
      setProgress(p => {
        if (p <= 0) { clearInterval(timer); return 0; }
        return p - step;
      });
    }, 100);

    // Auto dismiss
    const t2 = setTimeout(() => {
      setVisible(false);
      setTimeout(() => onDismiss(notification.id), 350);
    }, AUTO_DISMISS_MS);

    return () => { clearTimeout(t1); clearTimeout(t2); clearInterval(timer); };
  }, []);

  function handleDismiss() {
    setVisible(false);
    setTimeout(() => onDismiss(notification.id), 350);
  }

  const cfg = SOURCE_COLORS[notification.source] || SOURCE_COLORS.Manual;
  const fmtDate = d => new Date(d).toLocaleDateString("en-IN", { day: "numeric", month: "short" });
  const nights  = Math.ceil(
    (new Date(notification.checkOut) - new Date(notification.checkIn)) / 86400000
  );

  return (
    <div className={`
      transform transition-all duration-350 ease-out w-80
      ${visible ? "translate-x-0 opacity-100" : "translate-x-full opacity-0"}
    `}>
      <div className={`
        bg-white rounded-2xl shadow-2xl border border-gray-100
        ring-1 ${cfg.ring} overflow-hidden
      `}>
        {/* Progress bar */}
        <div className="h-0.5 bg-gray-100">
          <div
            className="h-full bg-gradient-to-r from-amber-400 to-orange-500 transition-all duration-100"
            style={{ width: `${progress}%` }}
          />
        </div>

        <div className="p-4">
          {/* Header */}
          <div className="flex items-start justify-between mb-3">
            <div className="flex items-center gap-2">
              <div className="relative">
                <div className="w-9 h-9 bg-amber-50 rounded-xl flex items-center justify-center">
                  <User size={18} className="text-amber-600" />
                </div>
                <span className={`absolute -top-1 -right-1 w-3 h-3 rounded-full
                  ${cfg.dot} ring-2 ring-white animate-pulse`} />
              </div>
              <div>
                <p className="text-xs font-bold text-gray-900 uppercase tracking-wider">
                  New Booking Request
                </p>
                <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full
                  ${cfg.bg} ${cfg.text}`}>
                  {notification.source}
                </span>
              </div>
            </div>
            <button onClick={handleDismiss} aria-label="Dismiss"
              className="-m-2.5 w-11 h-11 flex items-center justify-center hover:bg-gray-100 rounded-xl transition-colors flex-shrink-0">
              <X size={14} className="text-gray-400" />
            </button>
          </div>

          {/* Guest info */}
          <div className="space-y-2 mb-3">
            <div className="flex items-center gap-2">
              <User size={13} className="text-gray-400 flex-shrink-0" />
              <span className="text-sm font-semibold text-gray-900 truncate">
                {notification.guestName}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <Calendar size={13} className="text-gray-400 flex-shrink-0" />
              <span className="text-xs text-gray-600">
                {fmtDate(notification.checkIn)} → {fmtDate(notification.checkOut)}
                <span className="text-gray-400 ml-1">({nights}n)</span>
              </span>
            </div>
            {notification.whatsapp && (
              <div className="flex items-center gap-2">
                <Phone size={13} className="text-gray-400 flex-shrink-0" />
                <span className="text-xs text-gray-600">{notification.whatsapp}</span>
              </div>
            )}
          </div>

          {/* Ref */}
          <p className="text-[10px] font-mono text-gray-400 mb-3">{notification.bookingRef}</p>

          {/* Actions */}
          <div className="flex gap-2">
            <a
              href="/admin/bookings"
              onClick={handleDismiss}
              className="flex-1 flex items-center justify-center gap-1.5 min-h-[44px] rounded-xl
                bg-gradient-to-r from-amber-500 to-orange-500 text-white
                text-xs font-semibold hover:from-amber-600 hover:to-orange-600
                shadow-sm transition-all"
            >
              <ExternalLink size={11} /> Review
            </a>
            <button
              onClick={handleDismiss}
              className="px-4 min-h-[44px] rounded-xl border border-gray-200 text-xs
                text-gray-500 hover:bg-gray-50 transition-colors"
            >
              Later
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function BookingToastContainer() {
  const { notifications, dismiss } = useNotifications();

  if (notifications.length === 0) return null;

  return (
    // above-mobile-nav (index.css): at z-[100] this sat over the bottom tab
    // bar's right-hand end on phones and blocked taps on "More" for as long
    // as a toast was up. Desktop keeps the usual bottom corner.
    //
    // z-[45], not z-[100]: above page content and the tab bar (z-40), but
    // BELOW every dialog (z-50 and up). At 100 it covered whatever dialog was
    // open — on a phone a bottom sheet opens exactly where it sits, so while
    // a booking request was pending it swallowed the touches meant for the
    // dialog: found when the photo cropper could not be dragged or pinched.
    <div className="fixed above-mobile-nav right-6 z-[45] flex flex-col-reverse gap-3">
      {notifications.map(n => (
        <Toast key={n.id} notification={n} onDismiss={dismiss} />
      ))}
    </div>
  );
}
